"use client";

import { Redirect } from "@/components/atoms/Redirect";
import { useCampaign } from "@/lib/hooks/useChangeLocation/useChangeLocation";
import { useSelection } from "@/lib/hooks/useSelection";
import usePersistedStore from "@/lib/stateManagement/persistedState/persistedStore";
import { useQuery } from "@tanstack/react-query";
import { isNotFoundError } from "next/dist/client/components/not-found";
import { createContext, useContext, useMemo } from "react";
import { cachedServerFetchProductByMarket } from "../_actions";
import { PDPProduct } from "../page";

interface ProductContext {
  product: PDPProduct;
  isLoading: boolean;
}

export type Variants = NonNullable<PDPProduct>["variants"];

let lazyVariants: Variants = undefined;

export const ProductContext = createContext<ProductContext>(null!);

export function ProductProvider({
  children,
  initialProduct,
}: {
  children: React.ReactNode;
  initialProduct: ProductContext["product"];
}) {
  const { data: productWithLazyVariants, error } =
    useProductWithLazyVariants(initialProduct);
  const isInitialLoading = !productWithLazyVariants && !error;

  if (isNotFoundError(error)) {
    return <Redirect href="/not-found" replace />;
  }

  return (
    <ProductContext.Provider
      value={{
        product: productWithLazyVariants,
        isLoading: isInitialLoading,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export const useProductContext = () => {
  return useContext(ProductContext);
};

/**
 * This hook allows variants to "lag" behind the product data for the current market
 * Only if the variants area part of the initial product data loaded in the layout
 */
const useProductWithLazyVariants = (initialProduct: PDPProduct) => {
  const { isLoading, data } = useSelection();
  const campaign = useCampaign();

  const market = data?.location?.market;
  const token = usePersistedStore((state) => state.token);

  const { data: product, error } = useQuery({
    queryKey: ["product", { uri: initialProduct?.uri, token, market }],

    queryFn: async () => {
      const result = await cachedServerFetchProductByMarket({
        customerToken: token,
        uri: initialProduct?.uri!,
        market: Number(market)!,
      });

      // console.log("FROM API", result?.variants?.length);

      return result;
    },

    enabled:
      !!initialProduct &&
      !isLoading &&
      typeof market === "number" &&
      !!token &&
      !campaign.isCampaignLoading,

    staleTime: Infinity,
    retry: false,
  });

  if (product) {
    lazyVariants = product.variants;
  }

  const productWithLazyVariants = useMemo(() => {
    const resolvedProduct = product || initialProduct;

    if (!resolvedProduct) {
      return;
    }

    if (lazyVariants && variantsInProduct(initialProduct, lazyVariants)) {
      return {
        ...resolvedProduct,
        variants: lazyVariants,
      };
    }

    // Reset the lazy variants as we are obviously on a different product
    lazyVariants = undefined;

    return resolvedProduct;
  }, [product, initialProduct]);

  return { data: productWithLazyVariants, error, isLoading };
};

function variantsInProduct(product: PDPProduct, variants: Variants) {
  return variants?.every((variant) => {
    return product?.variants?.some((productVariant) => {
      return productVariant.uri === variant.uri;
    });
  });
}
